import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowRight } from "./ProjectIcons";
import { getButtonText, getResponseText } from "./TestActionFunctions";
import Spinner from "./Spinner";
import DynamicJSONCard from "./SchemaFormTemplates/DynamicJSONCard";

const customWidgets: Record<string, React.ComponentType<any>> = {
    DynamicJSONCard: DynamicJSONCard,
};

type TestActionProps = {
    actionId: string;
    actionValue: any;
    onTestAction: Function;
    isLoading: boolean;
    response: any | null;
    buttonText?: string;
};

const TestAction: React.FC<TestActionProps> = ({
    actionId,
    actionValue,
    onTestAction,
    isLoading,
    response,
    buttonText,
}) => {
    const { t } = useTranslation();
    const [isResponseVisible, setResponseVisible] = useState(true);

    // Get response text and success status
    const getResponseInfo = () => {
        if (!response) return [null, null];
        return getResponseText(actionValue, response, t);
    };

    // Get appropriate class names for response styling
    const getResponseClasses = () => {
        const [_, isSuccess] = getResponseInfo();

        if (isSuccess === null) return "bg-light-gray text-dark-gray";
        return isSuccess
            ? "bg-light-green text-dark-green"
            : "bg-very-light-orange text-dark-gray";
    };

    // Get pre tag class names for JSON display
    const getPreClasses = () => {
        const classes = getResponseClasses();
        return `${classes} p-2 rounded-sm text-xs w-full overflow-auto max-h-60`;
    };

    // Determine if a custom widget should be used for rendering
    const getWidgetComponent = () => {
        if (!response || !response.ui || !response.ui.widget) {
            return null;
        }

        const widgetName = response.ui.widget;
        return customWidgets[widgetName] || null;
    };

    // Render response details based on type
    const renderResponseDetails = () => {
        if (!isResponseVisible) return null;

        const WidgetComponent = getWidgetComponent();

        if (WidgetComponent && response.data) {
            // Render with the appropriate custom widget
            if (Array.isArray(response.data)) {
                // Handle array data
                return response.data.map((item: any, index: number) => (
                    <WidgetComponent key={index} {...item} />
                ));
            } else if (typeof response.data === 'object') {
                // Handle object data - common case for section-based widgets
                return Object.entries(response.data).map(([key, value]) => (
                    <WidgetComponent
                        key={key}
                        header={key}
                        content={value}
                    />
                ));
            }
        }

        // Default JSON display
        return (
            <pre className={getPreClasses()}>
                {JSON.stringify(response, null, 2)}
            </pre>
        );
    };

    return (
        <div className="flex flex-col">
            {/* Button row with response message */}
            <div className="flex flex-row items-center">
                <button
                    className="opacity-100 bg-light-gray hover:bg-gray-lines text-dark-gray font-semibold rounded py-2 px-4 ml-1 mr-3 shadow"
                    onClick={() => onTestAction(actionId, actionValue)}
                >
                    {buttonText || getButtonText(actionValue, t)}
                </button>

                {isLoading && <Spinner />}

                {!isLoading && response && (
                    <p className={`${getResponseClasses()} p-2 rounded-sm`}>
                        {getResponseInfo()[0]}
                    </p>
                )}
            </div>

            {/* Details section */}
            {!isLoading && response && (
                <div className="flex flex-col items-start w-full">
                    <button
                        className="ml-1 mt-2 text-dark-gray hover:text-dark-green flex items-center"
                        onClick={() => setResponseVisible(!isResponseVisible)}
                    >
                        {isResponseVisible
                            ? <ArrowDown />
                            : <ArrowRight />}
                        {t("View Details")}
                    </button>

                    {renderResponseDetails()}
                </div>
            )}
        </div>
    );
};

export default TestAction;